/**
 * Welches Eingabefeld eine Bedingung braucht -- F1-49, Etappe 3.
 *
 * Eigene Datei und nicht bei der Komponente, aus demselben Grund wie
 * ``conditions.ts``: Konstanten neben einer Komponente brechen das schnelle
 * Neuladen.
 */

import type { MasterDataKind } from '../masterdata/api';

export type ConditionValue = string | number | boolean | string[];

/**
 * Die Art des Werts je Feld aus ``automation/conditions.py`` (``FIELDS``).
 *
 * Was hier fehlt, bekommt ein Textfeld -- ein neues Feld auf dem Server
 * bleibt damit bedienbar, bevor jemand es hier eintraegt.
 */
export type ValueKind =
  | { kind: 'text' }
  | { kind: 'number' }
  | { kind: 'boolean' }
  | { kind: 'masterdata'; source: MasterDataKind; by: 'id' | 'name' }
  | { kind: 'category'; by: 'id' | 'name' }
  | { kind: 'user' }
  | { kind: 'customer'; by: 'id' | 'name' | 'number' }
  | { kind: 'contact' }
  | { kind: 'email' }
  | { kind: 'enum'; values: readonly string[]; labelKey: string };

const STATE_CATEGORIES = [
  'new',
  'open',
  'pending_customer',
  'pending_internal',
  'resolved',
  'closed',
] as const;

const SOURCES = ['email', 'portal', 'phone', 'web_form', 'api', 'automation'] as const;

const KINDS: Record<string, ValueKind> = {
  is_major_incident: { kind: 'boolean' },
  'customer.is_vip': { kind: 'boolean' },
  age_hours: { kind: 'number' },
  hours_since_update: { kind: 'number' },
  'priority.level': { kind: 'number' },
  source: { kind: 'enum', values: SOURCES, labelKey: 'ticketSource' },
  'state.category': { kind: 'enum', values: STATE_CATEGORIES, labelKey: 'stateCategory' },
  queue_id: { kind: 'masterdata', source: 'queue', by: 'id' },
  state_id: { kind: 'masterdata', source: 'state', by: 'id' },
  type_id: { kind: 'masterdata', source: 'type', by: 'id' },
  priority_id: { kind: 'masterdata', source: 'priority', by: 'id' },
  impact_id: { kind: 'masterdata', source: 'impact', by: 'id' },
  urgency_id: { kind: 'masterdata', source: 'urgency', by: 'id' },
  'queue.name': { kind: 'masterdata', source: 'queue', by: 'name' },
  'state.name': { kind: 'masterdata', source: 'state', by: 'name' },
  'type.name': { kind: 'masterdata', source: 'type', by: 'name' },
  'priority.name': { kind: 'masterdata', source: 'priority', by: 'name' },
  category_id: { kind: 'category', by: 'id' },
  'category.name': { kind: 'category', by: 'name' },
  owner_id: { kind: 'user' },
  responsible_id: { kind: 'user' },
  customer_id: { kind: 'customer', by: 'id' },
  'customer.name': { kind: 'customer', by: 'name' },
  'customer.number': { kind: 'customer', by: 'number' },
  contact_id: { kind: 'contact' },
  'contact.email': { kind: 'email' },
};

export function valueKindOf(field: string): ValueKind {
  return KINDS[field] ?? { kind: 'text' };
}

/** Ob ein Wert vollstaendig ist -- sonst bleibt "Bedingung hinzufuegen" gesperrt. */
export function isComplete(value: ConditionValue | null): boolean {
  if (value === null) return false;
  if (typeof value === 'string') return value.trim() !== '';
  if (typeof value === 'number') return !Number.isNaN(value);
  if (Array.isArray(value)) return value.length > 0;
  return true;
}
